// src/api.js
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_WHATSAPP_SERVICE_URL || 'http://localhost:3001';

const apiClient = axios.create({
  baseURL: `${BASE_URL}/api`,
  withCredentials: true, // Kept here in case you also use cookies
});

// ---------- ✅ REQUEST INTERCEPTOR (THE FIX) ----------
// This runs before every single request sent by apiClient
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      // Add the Bearer token to the Authorization header
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ---------- Error Interceptor ----------
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (!error.response) {
      const networkError = new Error('Network error – please check your connection');
      networkError.status = 0;
      return Promise.reject(networkError);
    }

    const { status, data } = error.response;
    let message = 'An unexpected error occurred';

    if (data && data.description) message = data.description;
    else if (data && data.error) message = data.error;
    else if (data && data.message) message = data.message;
    else if (status === 401) message = 'Unauthorized – please log in again';
    else if (status === 404) message = 'Resource not found';
    else if (status === 500) message = 'Server error – please try again later';

    const enhancedError = new Error(message);
    enhancedError.status = status;
    enhancedError.response = error.response;
    return Promise.reject(enhancedError);
  }
);

// ---------- AUTH ----------
export const signup = (name, email, password) =>
  apiClient.post('/auth/signup', { name, email, password }).then(r => r.data);

export const login = (email, password) =>
  apiClient.post('/auth/login', { email, password }).then(r => r.data);

export const logout = () =>
  apiClient.post('/auth/logout').then(r => r.data);

export const getMe = () =>
  apiClient.get('/auth/me').then(r => r.data);

// ---------- CONNECTIONS ----------
export const getConnections = () =>
  apiClient.get('/connections').then(r => r.data);

export const addConnection = (phoneNumber, name) =>
  apiClient.post('/connections', { phoneNumber, name }).then(r => r.data);

export const updateConnection = (connectionId, data) =>
  apiClient.put(`/connections/${connectionId}`, data).then(r => r.data);

export const deleteConnection = (connectionId) =>
  apiClient.delete(`/connections/${connectionId}`).then(r => r.data);

export const reconnectConnection = (connectionId) =>
  apiClient.post(`/connections/${connectionId}/reconnect`).then(r => r.data);

export const disconnectDevice = (connectionId) =>
  apiClient.post(`/connections/${connectionId}/disconnect`).then(r => r.data);

// ---------- MESSAGES ----------
export const sendMessage = (deviceToken, numbers, message, media = [], delay = null, schedule = null) =>
  apiClient.post('/sendmessage', { // <-- Changed endpoint here
    device_token: deviceToken,
    numbers,
    message,
    media,
    delay,
    schedule,
  }).then(r => r.data);

export const getMessages = (page = 1, limit = 10, search = '', status = '', connectionId = null) =>
  apiClient.get('/messages', {
    params: { page, limit, search, status, connection_id: connectionId },
  }).then(r => r.data);

// ---------- CAMPAIGNS ----------
export const getCampaigns = () =>
  apiClient.get('/campaigns').then(r => r.data);

export const getCampaign = (campaignId, page = 1, limit = 20) =>
  apiClient.get(`/campaigns/${campaignId}`, { params: { page, limit } }).then(r => r.data);

export const createCampaign = (data) =>
  apiClient.post('/campaigns', data).then(r => r.data);

export const updateCampaign = (campaignId, data) =>
  apiClient.put(`/campaigns/${campaignId}`, data).then(r => r.data);

export const deleteCampaign = (campaignId) =>
  apiClient.delete(`/campaigns/${campaignId}`).then(r => r.data);

export const startCampaign = (campaignId) =>
  apiClient.post(`/campaigns/${campaignId}/start`).then(r => r.data);

export const addCampaignRecipients = (campaignId, fromMobile, recipients) =>
  apiClient.post(`/campaigns/${campaignId}/recipients`, { from_mobile: fromMobile, recipients })
    .then(r => r.data);

export const uploadCampaignRecipients = (campaignId, fromMobile, file) => {
  const formData = new FormData();
  formData.append('from_mobile', fromMobile);
  formData.append('file', file);
  return apiClient.post(`/campaigns/${campaignId}/upload-recipients`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};

export const addCampaignMedia = (campaignId, media) =>
  apiClient.post(`/campaigns/${campaignId}/media`, media).then(r => r.data);


export const updateProfile = (data) =>
  apiClient.put('/auth/profile', data).then(r => r.data);


export const updateApiKey = () =>
  apiClient.put('/auth/api-key').then(r => r.data);

export const updatePassword = (data) =>
  apiClient.put('/auth/password', data).then(r => r.data);

// ---------- HEALTH ----------
// 💡 Note: This uses the raw axios import. If your backend requires auth for /health, 
// you will need to attach the token manually, or switch this to apiClient.
export const healthCheck = () =>
  axios.get(`${BASE_URL}/health`).then(r => r.data);